// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// ui.ts — the markup that is not the board: the menu, the reference card, the about panel and the
// run summary.
//
// THE SUMMARY IS A POST-MORTEM, NOT A SCOREBOARD. Every block is derived from the same event stream
// the mechanism audit reads, and the one it exists for is the chokepoint sentence — "every third
// journey this winter went through the gap by the stove" — which is a fact about the player's own
// layout that nobody could possibly derive by watching.

import { CAUSE_NAMES, Cls, CLS_NAMES, Res, SKILL_NAMES, State, chokepoint, livingCount } from './sim';
import { MODES, MODE_IDS, Mode, TICKS_PER_DAY } from './modes';
import { COLS, colOf, rowOf } from './map';

export const FOOTER =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>' +
  ' · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function menuHtml(selected: string): string {
  const modes = MODE_IDS.map((id) => {
    const m = MODES[id];
    return (
      `<button class="mode-btn" data-mode="${id}" aria-pressed="${id === selected}">` +
      `<span class="m-name">${m.name}</span>` +
      `<span class="m-rule">${m.rule}</span>` +
      `</button>`
    );
  }).join('');
  return (
    `<h1>Larder</h1>` +
    `<p class="tagline">The food is over there, the mouths are over here, and the snow is editing the distance between them.</p>` +
    `<div class="modes">${modes}</div>` +
    `<div class="menu-row">` +
    `<button class="primary" data-act="play">Begin the winter</button>` +
    `</div>` +
    `<div class="menu-row">` +
    `<button data-act="tutorial">How to play</button>` +
    `<button data-act="about">About</button>` +
    `<button data-act="mute"></button>` +
    `</div>`
  );
}

export function howToPlayHtml(): string {
  return (
    `<h2>How to play</h2>` +
    `<p>You do not command anyone directly. You <b>designate work</b> on the map — press and hold a tile to clear a drift, cut timber or build — and you <b>rank what work matters most</b> on the WORK board. Your colonists choose their own next job from that ranking and from how far they must walk to reach it.</p>` +
    `<p>Snow drifts against walls, so a narrow gap fills faster than an open one. A corridor that plugs cuts your food off from your people, and nobody will announce it — the haulers simply stop going. Watch the forecast, widen the artery before the storm, and keep the larder reachable.</p>` +
    `<p><b>Tap</b> a colonist to see the ground they can reach. <b>Tap</b> a store to shade the map by distance to it. <b>Press and hold</b> a tile for the action wheel. <b>Drag</b> to designate a whole rectangle at once.</p>` +
    `<p>Survive to dawn on the last day.</p>`
  );
}

export function aboutHtml(): string {
  return (
    `<h2>About</h2>` +
    `<p>Larder is a small colony simulation: people with needs, weather that keeps coming, and a supply chain that can fail in ways nobody wrote down. Everything you see is generated by code — the map, the glyphs, the icons and every sound.</p>` +
    `<p>It runs entirely in your browser. There is no account, no server and no game state anywhere but this device. No cookies and no fingerprinting; the only network call the page makes is an anonymous, cookie-less page-view count.</p>` +
    `<p>${FOOTER}</p>`
  );
}

function fmtTile(t: number): string {
  return `${String.fromCharCode(65 + colOf(t))}${rowOf(t) + 1}`;
}

function neighbourNames(s: State, t: number): string {
  const names: string[] = [];
  const around = [t - COLS, t + COLS, t - 1, t + 1].filter((x) => x >= 0 && x < s.build.length);
  for (const n of around) {
    const b = s.build[n];
    if (b === 5 + 1) names.push('the fire');
    else if (b === 6 + 1) names.push('the stove');
    else if (b === 4 + 1) names.push('the larder');
    else if (b === 2 + 1) names.push('the door');
  }
  return names.length > 0 ? ` by ${names[0]}` : '';
}

export function summaryHtml(s: State): string {
  const won = s.outcome === 1;
  const souls = livingCount(s);
  const days = Math.min(s.mode.days, Math.ceil(s.tick / TICKS_PER_DAY));

  const roll = [];
  for (let i = 0; i < s.n; i++) {
    const skill = `${SKILL_NAMES[s.skillKind[i]]} ${s.skillLvl[i]}`;
    if (s.alive[i]) {
      roll.push(
        `<li><b>${s.name[i]}</b> · ${skill} · carried ${s.hauled[i]} units, walked ${s.walked[i]} tiles · came through</li>`,
      );
    } else {
      roll.push(
        `<li class="dead"><b>${s.name[i]}</b> · ${skill} · carried ${s.hauled[i]} units · died of ${CAUSE_NAMES[s.deathCause[i]]}</li>`,
      );
    }
  }

  const choke = chokepoint(s);
  const total = s.traversal.reduce((a, b) => a + b, 0);
  const share = total > 0 && choke >= 0 ? Math.round((s.traversal[choke] * 100) / total) : 0;
  const chokeLine =
    choke >= 0 && share > 0
      ? `About one journey in ${Math.max(2, Math.round(100 / Math.max(1, share)))} this winter went through ${fmtTile(choke)}${neighbourNames(s, choke)}.`
      : 'Nobody walked far enough this winter to wear a path.';

  const severLine =
    s.worstSeverance > 0
      ? `Your stores were cut off from the house for ${Math.round(s.worstSeverance / 10)}s at the worst of it, and ${Math.round(s.severedTicks / 10)}s in total.`
      : 'The larder stayed reachable all winter. That is not luck — it is the whole job.';

  const walked = Array.from({ length: s.n }, (_, i) => s.walked[i]).reduce((a, b) => a + b, 0);

  return (
    `<h1 class="${won ? 'won' : 'lost'}">${won ? 'Overwintered' : 'The colony is lost'}</h1>` +
    `<p>${won ? `${souls} of ${s.n} came through ${s.mode.name}.` : `Everyone died. The winter reached day ${days} of ${s.mode.days}.`}</p>` +
    `<div class="block roll"><h3>The roll</h3><ul>${roll.join('')}</ul></div>` +
    `<div class="block"><h3>The ledger</h3><p>Cut ${s.produced[Res.Wood]} wood and gathered ${s.produced[Res.Food]} food. Cooked ${s.produced[Res.Meal]} meals and ate ${s.eaten}. Burned ${s.burned} wood keeping the fire in.</p></div>` +
    `<div class="block"><h3>The chokepoint</h3><p>${chokeLine}</p></div>` +
    `<div class="block"><h3>What it cost you</h3><p>${severLine}</p>` +
    `<p>Your colonists spent ${walked} tiles of walking this winter. Every one of those was a decision you made about where things live.</p></div>` +
    `<div class="menu-row">` +
    `<button class="primary" data-act="again">Another winter</button>` +
    `<button data-act="menu">Menu</button>` +
    `<button class="results-feedback" data-act="feedback">Feedback</button>` +
    `</div>`
  );
}

/** The WORK board rows: priority, how many are waiting, and how old the oldest one is. */
export function workRows(s: State): string {
  return s.rank
    .map((cls, i) => {
      let waiting = 0;
      let oldest = 0;
      for (const j of s.jobs) {
        if (j.done || j.cls !== cls) continue;
        if (j.claimedBy >= 0) continue;
        waiting++;
        oldest = Math.max(oldest, s.tick - j.createdTick);
      }
      const meta =
        waiting === 0
          ? 'nothing waiting'
          : `${waiting} waiting · oldest ${Math.round(oldest / 10)}s`;
      return (
        `<div class="workrow" data-cls="${cls}">` +
        `<span class="wname">${CLS_NAMES[cls].toUpperCase()}</span>` +
        `<span class="wmeta">${meta}</span>` +
        `<button data-up="${i}" aria-label="Raise ${CLS_NAMES[cls]}"${i === 0 ? ' disabled' : ''}>&#9650;</button>` +
        `<button data-down="${i}" aria-label="Lower ${CLS_NAMES[cls]}"${i === s.rank.length - 1 ? ' disabled' : ''}>&#9660;</button>` +
        `</div>`
      );
    })
    .join('');
}

export { Cls };
export type { Mode };
